import { createFileRoute, Link } from "@tanstack/react-router";
import { Cog6ToothIcon } from "@heroicons/react/24/outline";
import { useLoginStatus } from "../../hooks/useLoginStatus";
import { useProfilePicPath } from "../../hooks/useProfilePicPath";
import { useLanguageSpace } from "../../hooks/useLanguageSpace";
import { TEXT_LANGUAGES } from "../../constants";
import useTranslation from "../../hooks/useTranslation";
import { useVocabularyStatistics } from "../../hooks/useVocabularyStatistics";
import { useTextStatistics } from "../../hooks/useTextStatistics";

export const Route = createFileRoute("/app/profile")({
  component: UserProfilePage,
});

function UserProfilePage() {
  const loginStatus = useLoginStatus();
  const profilePicPath = useProfilePicPath();
  const { languageSpace } = useLanguageSpace("ENGLISH");
  const { translate, language } = useTranslation();
  const vocabularyStatistics = useVocabularyStatistics();
  const textStatistics = useTextStatistics();

  if (!loginStatus.isSuccess || loginStatus.data.state === "logged out") {
    return <></>;
  }
  if (!vocabularyStatistics.isSuccess || !textStatistics.isSuccess) {
    return <div className="px-4 py-5 text-sm text-slate-500">loading</div>;
  }

  const currentSpace = languageSpace === "ALL" ? "ENGLISH" : languageSpace;
  const currentLanguage = TEXT_LANGUAGES.find(
    (language) => language.id === currentSpace,
  );
  const nativeLanguageLabelById: Record<string, string> = {
    ENGLISH: "English",
    ESPERANTO: "Esperanto",
    FRENCH: "Français",
    GERMAN: "Deutsch",
    GREEK: "Ελληνικά",
    GREEK_LATIN_TRANSCRIPTION: "Ελληνικά (Latin)",
    ITALIAN: "Italiano",
    PORTUGUESE_FROM_BRAZIL: "Português (Brasil)",
    PORTUGUESE_FROM_PORTUGAL: "Português (Portugal)",
    RUSSIAN: "Русский",
    RUSSIAN_LATIN_TRANSCRIPTION: "Русский (Latin)",
    SPANISH: "Español",
  };
  const currentNativeLanguageLabel =
    (currentLanguage && nativeLanguageLabelById[currentLanguage.id]) ?? "English";
  const numberLocale = language === "GERMAN" ? "de-DE" : "en-US";
  const formatNumber = (value: number) => new Intl.NumberFormat(numberLocale).format(value);

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-5 md:px-6 md:py-6">
      <header className="flex items-start justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <img
            src={profilePicPath()}
            alt={translate("profile_image_alt")}
            className="h-12 w-12 rounded-full border border-slate-200 object-cover"
          />
          <div>
            <p className="text-sm text-slate-500">@{loginStatus.data.username}</p>
            <h1 className="text-lg font-semibold text-slate-900">
              {currentNativeLanguageLabel}
            </h1>
          </div>
        </div>
        <Link
          to="/app/space_settings"
          aria-label={translate("space_settings_open")}
          className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
        >
          <Cog6ToothIcon className="h-5 w-5" aria-hidden="true" />
        </Link>
      </header>
      <section className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-semibold text-slate-500">
            {translate("statistics_header_texts")}
          </p>
          <div className="mt-3 flex items-center justify-between gap-4">
            <div>
              <p className="text-2xl font-semibold text-slate-900">
                {formatNumber(textStatistics.data.total_created_texts)}
              </p>
              <p className="text-sm text-slate-500">
                {translate("statistics_total_texts_created")}
              </p>
            </div>
            <div className="h-10 w-px bg-slate-200" />
            <div>
              <p className="text-2xl font-semibold text-slate-900">
                {formatNumber(textStatistics.data.total_created_texts_length)}
              </p>
              <p className="text-sm text-slate-500">
                {translate("statistics_total_word_count")}
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-semibold text-slate-500">
            {translate("statistics_header_vocabulary")}
          </p>
          <div className="mt-3 flex items-center justify-between gap-4">
            <div>
              <p className="text-2xl font-semibold text-slate-900">
                {formatNumber(vocabularyStatistics.data.total_created)}
              </p>
              <p className="text-sm text-slate-500">
                {translate("statistics_total_words_added")}
              </p>
            </div>
            <div className="h-10 w-px bg-slate-200" />
            <div>
              <p className="text-2xl font-semibold text-slate-900">
                {formatNumber(vocabularyStatistics.data.last_four_weeks_created)}
              </p>
              <p className="text-sm text-slate-500">
                {translate("statistics_total_words_added_last_four_weeks")}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="mt-4">
        <Link
          to="/app/vocabulary_progress"
          search={{ tab: undefined }}
          className="inline-flex items-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
        >
          {translate("space_settings_vocabulary")}
        </Link>
      </div>
    </div>
  );
}
